import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const COMPANY = {
  name: 'Swiss Chem International',
  address: '82-C Mehar Sadiq Market, Railway Road, Faisalabad',
  phone: '+92 300 7671404',
  landline: '041-2611464',
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    .replace(/ /g, '-');
};

const fmtQty = (n) => {
  const num = parseFloat(n) || 0;
  return num.toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

/**
 * Generate and download a K&M-style Item Ledger PDF (stock movement).
 * @param {object} ledger - result of GET /api/chemical/item-ledger/:scope/:id
 * @param {object} opts - { from_date, to_date }
 */
export const generateItemLedgerPdf = (ledger, opts = {}) => {
  const { scope, info, opening_qty, transactions, total_in, total_out, closing_qty, month_breakup } = ledger;
  const { from_date, to_date } = opts;

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 10;
  let y = 12;

  const unit = info.unit || '';

  // ─── Header ─────────────────────────────────
  doc.setDrawColor(40, 40, 40);
  doc.setLineWidth(0.4);
  doc.rect(marginX, y - 4, 12, 12);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(20, 20, 20);
  doc.text('SC', marginX + 6, y + 3.5, { align: 'center' });

  doc.setFontSize(9);
  doc.text(COMPANY.name, marginX + 15, y + 1);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(20, 20, 20);
  doc.text('Item Ledger', pageWidth / 2, y, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(scope === 'batch' ? '(Batch-wise)' : '(Product-wise)', pageWidth / 2, y + 5, { align: 'center' });

  doc.setFontSize(7);
  doc.setTextColor(90, 90, 90);
  const rightX = pageWidth - marginX;
  doc.text(`Printed on: ${new Date().toLocaleString('en-GB')}`, rightX, y - 2, { align: 'right' });

  // ─── From / To — centered ───
  y += 11;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(40, 40, 40);
  const fromStr = from_date ? formatDate(from_date) : '—';
  const toStr = to_date ? formatDate(to_date) : new Date().toLocaleDateString('en-GB').replace(/\//g, '-');
  doc.text(`From : ${fromStr}    To : ${toStr}`, pageWidth / 2, y, { align: 'center' });

  // ─── Thin divider ───
  y += 4;
  doc.setDrawColor(160, 160, 160);
  doc.setLineWidth(0.3);
  doc.line(marginX, y, pageWidth - marginX, y);

  // ─── Item info + opening ───
  y += 6;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(26, 60, 110);
  const itemLabel = scope === 'batch'
    ? `Batch : ${info.batch_no}  ·  ${info.product_code} — ${info.product_name}`
    : `Item : ${info.product_code} — ${info.product_name}`;
  doc.text(itemLabel, marginX, y);
  doc.setTextColor(40, 40, 40);
  doc.text(`Opening -->  ${fmtQty(opening_qty)} ${unit}`, rightX, y, { align: 'right' });

  // ─── Bold divider before table ───
  y += 4;
  doc.setDrawColor(40, 40, 40);
  doc.setLineWidth(0.6);
  doc.line(marginX, y, pageWidth - marginX, y);
  y += 4;

  // ─── Table ──────────────────────────────────
  const rows = transactions.map(t => [
    formatDate(t.date),
    t.narration,
    scope === 'batch' ? (t.ref_no || '—') : (t.batch_no || '—'),
    t.qty_in ? fmtQty(t.qty_in) : '',
    t.qty_out ? fmtQty(t.qty_out) : '',
    `${fmtQty(t.balance)} ${unit}`,
  ]);

  autoTable(doc, {
    startY: y,
    head: [['Date', 'Narration', scope === 'batch' ? 'Ref #' : 'Batch #', `In (${unit})`, `Out (${unit})`, 'Balance']],
    body: rows.length ? rows : [['—', 'No stock movement', '—', '—', '—', '—']],
    foot: [['', '', 'Total > > >', fmtQty(total_in), fmtQty(total_out), '']],
    theme: 'grid',
    headStyles: { fillColor: [235, 235, 235], textColor: [30, 30, 30], fontStyle: 'bold', fontSize: 7.5, lineColor: [160, 160, 160], lineWidth: 0.2 },
    bodyStyles: { fontSize: 7, textColor: [30, 30, 30], lineColor: [180, 180, 180], lineWidth: 0.15, valign: 'top' },
    footStyles: { fillColor: [245, 245, 245], textColor: [30, 30, 30], fontStyle: 'bold', fontSize: 7.5, lineColor: [160, 160, 160], lineWidth: 0.2 },
    didParseCell: (data) => {
      if (data.section !== 'body') return;
      if (data.column.index === 3 && data.cell.raw) data.cell.styles.textColor = [39, 174, 96];   // In green
      if (data.column.index === 4 && data.cell.raw) data.cell.styles.textColor = [192, 57, 43];   // Out red
      if (data.column.index === 5) data.cell.styles.textColor = [26, 60, 110];                     // Balance blue
    },
    columnStyles: {
      0: { cellWidth: 18 },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 26, halign: 'center' },
      3: { cellWidth: 22, halign: 'right' },
      4: { cellWidth: 22, halign: 'right' },
      5: { cellWidth: 28, halign: 'right' },
    },
    margin: { left: marginX, right: marginX },
  });

  let afterY = doc.lastAutoTable.finalY + 6;

  // ─── Bold divider after table ───
  doc.setDrawColor(40, 40, 40);
  doc.setLineWidth(0.5);
  doc.line(marginX, afterY - 3, pageWidth - marginX, afterY - 3);

  // ─── Closing balance ───
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(40, 40, 40);
  doc.text(`Closing Balance > > >   ${fmtQty(closing_qty)} ${unit}`, rightX, afterY, { align: 'right' });
  afterY += 8;

  // ─── Month breakup box ───
  if (month_breakup && month_breakup.length > 0) {
    const boxW = 55;
    const boxX = rightX - boxW;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(26, 60, 110);
    doc.setDrawColor(120, 150, 190);
    doc.setLineWidth(0.3);
    doc.text('Breakup', boxX, afterY);
    afterY += 2;
    doc.rect(boxX, afterY, boxW, month_breakup.length * 5 + 2);
    doc.setLineWidth(0.15);
    doc.setDrawColor(200, 200, 200);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(40, 40, 40);
    let by = afterY + 4;
    month_breakup.forEach((m, i) => {
      doc.text(m.month, boxX + 2, by);
      doc.setTextColor(m.net >= 0 ? 39 : 192, m.net >= 0 ? 174 : 57, m.net >= 0 ? 96 : 43);
      doc.text(fmtQty(m.net), boxX + boxW - 2, by, { align: 'right' });
      doc.setTextColor(40, 40, 40);
      if (i < month_breakup.length - 1) {
        doc.line(boxX, afterY + 2 + (i + 1) * 5, boxX + boxW, afterY + 2 + (i + 1) * 5);
      }
      by += 5;
    });
  }

  // ─── Page number ───
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text('Page 1 of 1', rightX, pageHeight - 6, { align: 'right' });

  const nameForFile = scope === 'batch' ? info.batch_no : info.product_code;
  doc.save(`ItemLedger_${String(nameForFile).replace(/\s+/g, '_')}.pdf`);
};